import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Callout, ErrorNote } from '../../components/ui/callout'
import { Card } from '../../components/ui/card'
import { Label, Skeleton, Textarea } from '../../components/ui/primitives'
import { ApiError, api } from '../../lib/api'
import { formatDate } from '../../lib/ui'

interface PublicApproval {
  ticket_ref: string
  ticket_title: string
  requester_name: string
  requester_email: string
  organization: string
  approver_name: string
  approver_email: string
  service: { service: string; subcategory: string } | null
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired'
  decision: string | null
  decision_source: string | null
  comment: string
  reason: string
  message: string
  requested_at: string
  decided_at: string | null
  expires_at: string | null
  can_decide: boolean
}

export function PublicApprovalPage() {
  const { token = '', action } = useParams<{ token: string; action?: string }>()
  const [comment, setComment] = useState('')
  const [result, setResult] = useState<PublicApproval | null>(null)

  const approval = useQuery({
    queryKey: ['public-approval', token],
    queryFn: () => api.get<PublicApproval>(`/approval-links/${token}/`),
  })

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      api.postJson<PublicApproval>(`/approval-links/${token}/${decision}/`, { comment }),
    onSuccess: (payload) => setResult(payload),
  })

  const data = result ?? approval.data
  const suggested = action === 'reject' ? 'reject' : action === 'approve' ? 'approve' : null

  if (approval.isLoading) {
    return (
      <main className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center p-4">
        <Skeleton className="h-72 w-full" />
      </main>
    )
  }
  if (approval.error || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center p-4">
        <ErrorNote
          message={
            approval.error instanceof ApiError
              ? approval.error.detail
              : 'This approval link is not valid.'
          }
        />
      </main>
    )
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center p-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs text-muted-foreground">{data.ticket_ref}</p>
            <h1 className="mt-1 text-lg font-semibold tracking-tight">{data.ticket_title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {data.organization} · {data.requester_name} ({data.requester_email})
            </p>
          </div>
          <Badge variant={data.status === 'pending' ? 'warning' : data.status === 'approved' ? 'success' : 'critical'}>
            {data.status.replace(/_/g, ' ')}
          </Badge>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Approver</dt>
            <dd>{data.approver_name}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Approver email</dt>
            <dd>{data.approver_email}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Requested</dt>
            <dd>{formatDate(data.requested_at)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Expires</dt>
            <dd>{data.expires_at ? formatDate(data.expires_at) : 'No expiry'}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted-foreground">Reason</dt>
            <dd>{data.reason || 'Manager approval is required before IT can act.'}</dd>
          </div>
          {data.message && (
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">Message</dt>
              <dd>{data.message}</dd>
            </div>
          )}
        </dl>

        {result && (
          <Callout tone={result.status === 'approved' ? 'info' : 'critical'} className="mt-4">
            {result.status === 'approved' ? 'Approved successfully.' : 'Rejected successfully.'}
          </Callout>
        )}

        {data.can_decide && !result ? (
          <div className="mt-4 border-t border-border pt-4">
            <Label htmlFor="approval-comment">Comment</Label>
            <Textarea
              id="approval-comment"
              rows={3}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              disabled={decide.isPending}
            />
            {decide.error && (
              <ErrorNote
                className="mt-3"
                message={
                  decide.error instanceof ApiError
                    ? decide.error.detail
                    : 'The approval decision could not be recorded.'
                }
              />
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={() => decide.mutate('approve')}
                disabled={decide.isPending}
                variant={suggested === 'approve' || suggested === null ? 'default' : 'outline'}
              >
                <CheckCircle2 />
                Approve
              </Button>
              <Button
                onClick={() => decide.mutate('reject')}
                disabled={decide.isPending}
                variant={suggested === 'reject' ? 'default' : 'outline'}
              >
                <XCircle />
                Reject
              </Button>
            </div>
          </div>
        ) : (
          !result && (
            <Callout tone="info" className="mt-4">
              This approval cannot be decided from this link.
            </Callout>
          )
        )}
      </Card>
    </main>
  )
}
